// Модули портала в компонентах: реактивный «включён ли модуль» + фильтры по реестру.
//
//   const { has, filterReportTypes, isEventKindVisible } = useModules()
//   <div v-if="has('tacho')">…</div>
//
// Источник истины: isModuleEnabled() из authService (disabledModules владельца +
// parentDisabledModules субпользователя из localStorage). Реактивность: authService
// шлёт window-событие portal:user-changed при каждом persistUser/clearAccessToken,
// здесь оно бампает version, и все computed пересчитываются без перезагрузки.
import { computed, ref } from 'vue'
import { isModuleEnabled } from '../services/authService'
import { MODULES, MODULE_KEYS, collectHidden } from '../modules/registry'

export const USER_CHANGED_EVENT = 'portal:user-changed'

const version = ref(0)

if (typeof window !== 'undefined') {
  const bump = () => { version.value += 1 }
  window.addEventListener(USER_CHANGED_EVENT, bump)
  // Другая вкладка перелогинилась или обновила профиль
  window.addEventListener('storage', bump)
}

// Выключенные модули текущего зрителя (общий computed на всё приложение)
const disabled = computed(() => {
  void version.value
  return MODULE_KEYS.filter((key) => !isModuleEnabled(key))
})

const hiddenReportCodes = computed(() => collectHidden(disabled.value, 'reportCodes'))
const hiddenEventKinds = computed(() => collectHidden(disabled.value, 'eventKinds'))
const hiddenPrefTypes = computed(() => collectHidden(disabled.value, 'prefTypes'))
const hiddenInAppTypes = computed(() => collectHidden(disabled.value, 'inAppTypes'))
const hiddenTabs = computed(() => collectHidden(disabled.value, 'notificationTabs'))
const hiddenWidgetTypes = computed(() => collectHidden(disabled.value, 'widgets'))
const hiddenDeviceFields = computed(() => collectHidden(disabled.value, 'deviceFields'))
const hiddenPermissions = computed(() => collectHidden(disabled.value, 'permissions'))

export function useModules() {
  // Включён ли модуль (неизвестный ключ считается включённым, как и на сервере)
  const has = (key) => !disabled.value.includes(key)

  // Список типов отчётов ({ code }) без отчётов выключенных модулей
  const filterReportTypes = (list) => (list || []).filter((r) => !hiddenReportCodes.value.has(r?.code))

  // Список типов матрицы уведомлений без типов выключенных модулей
  const filterPrefTypes = (list) => (list || []).filter((type) => !hiddenPrefTypes.value.has(String(type)))

  // Событие трека принадлежит включённому модулю (kind сравнивается без учёта регистра)
  const isEventKindVisible = (kind) => !hiddenEventKinds.value.has(String(kind || '').toUpperCase())

  // In-app уведомление (колокольчик) принадлежит включённому модулю
  const isInAppTypeVisible = (type) => !hiddenInAppTypes.value.has(String(type || ''))

  // Вкладка страницы уведомлений видна
  const isTabVisible = (tabKey) => !hiddenTabs.value.has(tabKey)

  // Виджет дашборда (etoll-balance и т. п.) относится к включённому модулю
  const isWidgetVisible = (type) => !hiddenWidgetTypes.value.has(String(type || ''))

  // Поле устройства из GET /devices (tacho, dtc) относится к включённому модулю
  const isDeviceFieldVisible = (field) => !hiddenDeviceFields.value.has(field)

  // Права субпользователей ({ key }) без прав выключенных модулей
  const filterPermissions = (list) => (list || []).filter((p) => !hiddenPermissions.value.has(p?.key))

  return {
    modules: MODULES,
    disabled,
    has,
    filterReportTypes,
    filterPrefTypes,
    isEventKindVisible,
    isInAppTypeVisible,
    isTabVisible,
    isWidgetVisible,
    isDeviceFieldVisible,
    filterPermissions,
  }
}
